import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDiamondExclamation } from "@fortawesome/sharp-solid-svg-icons"
import { Box, Typography } from "@mui/material"
import { useAxesTooltip } from "@mui/x-charts-pro"
import type { WastewaterPCRActivityLevel } from "@phc/common/gen/threatAnalyticsGen"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import {
  ActivityChip,
  InfoRow,
  ItemContainer,
  ItemHeader,
  StyledDivider,
  TooltipContainer,
  TooltipContentBox,
} from "../Charts/LegendStyles"
import {
  formatScientific,
  getLevel,
  hasActivity,
  makeAnomalyKey,
} from "./WastewaterUtils"
import { WASTEWATER_CHART_COLORS } from "./wastewaterHeatmapConstants"

export const WastewaterTooltipContent = ({
  rawData,
  hiddenIds,
  valueKey = "fecal_normalized_target_copies_per_l",
  anomalyKeys,
}: {
  rawData: WastewaterPCRActivityLevel[] | undefined
  hiddenIds: Set<string>
  valueKey?: keyof WastewaterPCRActivityLevel
  anomalyKeys?: Set<string>
}) => {
  const tooltipData = useAxesTooltip()
  const selectedDate = tooltipData?.[0]?.axisValue

  if (!tooltipData || tooltipData.length === 0 || !selectedDate) return null

  const tooltipContent = rawData?.filter(analysis => {
    const analysisDate = dayjsUTC(analysis.sampling_end_date).toDate()
    return (
      selectedDate instanceof Date &&
      analysisDate.getTime() === selectedDate.getTime()
    )
  })

  if (!tooltipContent || tooltipContent.length === 0) return null

  const relevantItems = tooltipContent
    .filter(analysis => !hiddenIds.has(analysis.target_name))
    .map(analysis => ({
      seriesId: analysis.target_name,
      analysis,
    }))

  if (relevantItems.length === 0) return null

  return (
    <TooltipContainer>
      <TooltipContentBox>
        <Typography
          align="center"
          variant="body2Bold"
        >{`Sampled ${dayjsUTC(selectedDate).format("M/DD/YY")}`}</Typography>

        {relevantItems.map((item, index) => {
          const { analysis } = item
          const level = getLevel(analysis)
          const hasAnomaly =
            anomalyKeys?.has(
              makeAnomalyKey(analysis.target_name, analysis.sampling_end_date)
            ) ?? false

          const levelText = hasActivity(level)
            ? `Activity Level - ${level}`
            : level

          const isRare = valueKey === "fecal_normalized_target_copies_per_l"

          return (
            <Box
              key={item.seriesId}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 0.75,
              }}
            >
              {index > 0 && <StyledDivider />}

              <ItemContainer>
                <ItemHeader>
                  <Typography variant="small1">{item.seriesId}</Typography>
                  {hasAnomaly && (
                    <FontAwesomeIcon
                      icon={faDiamondExclamation}
                      color={WASTEWATER_CHART_COLORS[level].light}
                      style={{ fontSize: 11 }}
                    />
                  )}
                </ItemHeader>

                <ActivityChip level={level}>{levelText}</ActivityChip>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <InfoRow>
                    <Typography variant="tiny" color={extraColors.medium}>
                      Sample Site
                    </Typography>
                    <Typography variant="tiny">
                      {analysis.phc_site_id || "N/A"}
                    </Typography>
                  </InfoRow>
                  <InfoRow>
                    <Typography variant="tiny" color={extraColors.medium}>
                      {isRare ? "Copies/L Norm" : "Copies/L Norm LOD"}
                    </Typography>
                    <Typography variant="tiny">
                      {typeof analysis[valueKey] === "number"
                        ? formatScientific(analysis[valueKey])
                        : "N/A"}
                    </Typography>
                  </InfoRow>
                </Box>
              </ItemContainer>
            </Box>
          )
        })}
      </TooltipContentBox>
    </TooltipContainer>
  )
}
